import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/our-story", label: "Our Story" },
  { to: "/destinations", label: "Study Destinations" },
  { to: "/ecosystem", label: "Medics Ecosystem" },
  { to: "/beware-scams", label: "Beware of Scams" },
  { to: "/contact", label: "Contact Us" },
];

const listV = {
  hidden: {},
  show: { transition: { staggerChildren: 0.055, delayChildren: 0.12 } },
  exit: {},
};
const itemV = {
  hidden: { x: 30, opacity: 0 },
  show: { x: 0, opacity: 1, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
  exit: { x: 20, opacity: 0, transition: { duration: 0.2 } },
};

export const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Auto show/hide on scroll: hide when scrolling down past a threshold, show on scroll up.
  useEffect(() => {
    let last = window.scrollY;
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      if (open || y < 140) setHidden(false);
      else if (y > last + 8) setHidden(true);
      else if (y < last - 8) setHidden(false);
      last = y;
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [open]);

  return (
    <>
      <motion.header
        initial={false}
        animate={{ y: hidden ? "-110%" : "0%" }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="fixed inset-x-0 top-0 z-[60]"
        data-testid="site-header"
      >
        <div className="bg-peach">
          <div className="mx-auto grid max-w-[1500px] grid-cols-[1fr_auto_1fr] items-center px-4 py-3 sm:px-6 lg:px-10">
            <div />
            <Link to="/" className="justify-self-center font-head text-2xl sm:text-3xl tracking-tight" data-testid="brand-wordmark">
              <span className="text-[var(--blue)]">Wurkwise</span>{" "}
              <span className="text-[#8fae2e]">Medics.</span>
            </Link>

            <div className="flex items-center justify-end">
              <button
                onClick={() => setOpen((v) => !v)}
                aria-label={open ? "Close menu" : "Open menu"}
                data-testid="menu-toggle"
                className="relative z-[70] flex h-11 w-11 items-center justify-center rounded-full transition-colors hover:bg-black/5"
              >
                <span className="relative block h-4 w-6">
                  <motion.span
                    className="absolute left-0 top-0 block h-[2px] w-6 rounded-full bg-[var(--ink)]"
                    animate={open ? { rotate: 45, y: 7 } : { rotate: 0, y: 0 }}
                    transition={{ duration: 0.3 }}
                  />
                  <motion.span
                    className="absolute left-0 top-[7px] block h-[2px] w-6 rounded-full bg-[var(--ink)]"
                    animate={open ? { opacity: 0, x: -8 } : { opacity: 1, x: 0 }}
                    transition={{ duration: 0.2 }}
                  />
                  <motion.span
                    className="absolute left-0 bottom-0 block h-[2px] w-6 rounded-full bg-[var(--ink)]"
                    animate={open ? { rotate: -45, y: -7 } : { rotate: 0, y: 0 }}
                    transition={{ duration: 0.3 }}
                  />
                </span>
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-[55]" data-testid="menu-overlay">
            {/* dim overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              onClick={() => setOpen(false)}
              className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
              data-testid="menu-backdrop"
            />

            {/* right pane */}
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              className="absolute right-0 top-[64px] bottom-0 flex w-[86%] flex-col overflow-hidden rounded-tl-[44px] border-l border-t border-white/10 bg-space shadow-[0_0_60px_rgba(0,0,0,0.5)] sm:w-[420px] lg:w-1/3"
              data-testid="menu-pane"
            >
              <div className="stars" />
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(500px_400px_at_100%_0%,rgba(63,156,228,0.16),transparent)]" />

              <div className="relative z-10 flex h-full flex-col overflow-y-auto px-8 pt-8 pb-8 sm:px-10">
                <span className="mb-5 text-xs uppercase tracking-[0.28em] text-white/40">Menu</span>

                <motion.nav variants={listV} initial="hidden" animate="show" exit="exit" className="flex flex-col">
                  {LINKS.map((l, i) => (
                    <motion.div key={l.to} variants={itemV}>
                      <Link
                        to={l.to}
                        data-testid={`menu-link-${l.to}`}
                        className="group flex items-center gap-3 py-1.5"
                      >
                        <ArrowUpRight
                          className="h-5 w-5 shrink-0 -translate-x-1 opacity-40 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100"
                          style={{ color: i % 2 === 0 ? "var(--blue)" : "#8fae2e" }}
                        />
                        <span className={`font-head text-2xl leading-tight tracking-tight transition-all duration-300 group-hover:translate-x-1 ${pathname === l.to ? "text-[var(--blue)]" : "text-white group-hover:text-[var(--blue)]"}`}>
                          {l.label}
                        </span>
                      </Link>
                    </motion.div>
                  ))}
                </motion.nav>

                <div className="mt-8 shrink-0 space-y-3 border-t border-white/10 pt-6">
                  <div className="flex flex-col gap-3">
                    <div
                      data-testid="menu-login"
                      aria-disabled="true"
                      className="flex cursor-default items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3 text-center text-sm font-semibold text-white/45"
                    >
                      Login / Sign up
                      <span className="rounded-full bg-[var(--lime)]/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--lime)]">
                        Coming soon
                      </span>
                    </div>
                    <Link
                      to="/contact"
                      data-testid="menu-book"
                      className="group inline-flex items-center justify-center gap-2 rounded-full bg-[var(--blue)] px-6 py-3 text-sm font-semibold text-white glow-blue"
                    >
                      Book Counselling
                      <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </motion.aside>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
